import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useDeleteUser,
  useResetUserPassword,
  useRevokeUserToken,
  useUpdateUser,
  useUsers,
} from '../api/queries';
import {
  ROLE_LABELS,
  type User,
  type UserRole,
  type UserStatus,
  type WorldOption,
} from '../api/types';
import { StatusBadge } from '../components/Badge';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { useDebounced } from '../hooks/useDebounced';

const STATUS_OPTIONS: Array<{ value: UserStatus | ''; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'pending', label: 'Wartet auf Freischaltung' },
  { value: 'active', label: 'Freigeschaltet' },
  { value: 'disabled', label: 'Gesperrt' },
];

const ROLE_OPTIONS: Array<{ value: UserRole | ''; label: string }> = [
  { value: '', label: 'Alle Rollen' },
  { value: 'user', label: 'Mitglied' },
  { value: 'tribeLeader', label: 'Stammesführer' },
  { value: 'admin', label: 'Administrator' },
];

function formatDate(value: string | null, withTime = false): string {
  if (!value) return '—';
  const date = new Date(value);
  return withTime ? date.toLocaleString('de-DE') : date.toLocaleDateString('de-DE');
}

export function AdminUsersPage({ currentUser }: { currentUser: User }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebounced(search);

  const status = (searchParams.get('status') ?? '') as UserStatus | '';
  const role = (searchParams.get('rolle') ?? '') as UserRole | '';
  const welt = searchParams.get('welt') ?? '';

  // Der entprellte Suchbegriff landet in der URL, damit Filter teilbar sind
  // und ein Neuladen der Seite denselben Ausschnitt zeigt.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set('q', debouncedSearch);
    else next.delete('q');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [debouncedSearch, searchParams, setSearchParams]);

  const filter = { q: debouncedSearch, status, role, welt };
  const users = useUsers(filter);
  const toast = useToast();
  const worlds = users.data?.worlds ?? [];

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const resetFilter = () => {
    setSearch('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const stats = users.data?.stats;
  const rows = users.data?.users ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Benutzerverwaltung</h1>
          <p className="muted">
            Neue Konten sind gesperrt, bis du sie hier freischaltest. Ohne Weltfilter siehst du
            alle Welten auf einmal.
          </p>
        </div>

        {stats && (
          <div className="stat-row compact">
            <div className="stat">
              <span className="stat-value stat-warn">{stats.pending}</span>
              <span className="stat-label">offen</span>
            </div>
            <div className="stat">
              <span className="stat-value stat-ok">{stats.active}</span>
              <span className="stat-label">aktiv</span>
            </div>
            <div className="stat">
              <span className="stat-value">{stats.disabled}</span>
              <span className="stat-label">gesperrt</span>
            </div>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <input
          type="search"
          placeholder="Benutzername suchen"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={welt} onChange={(event) => setParam('welt', event.target.value)}>
          <option value="">Alle Welten</option>
          <option value="global">Weltübergreifend (Administration)</option>
          {worlds.map((world) => (
            <option key={world.key} value={world.key}>
              {world.name}
            </option>
          ))}
        </select>
        <select value={status} onChange={(event) => setParam('status', event.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select value={role} onChange={(event) => setParam('rolle', event.target.value)}>
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-ghost" onClick={resetFilter}>
          Zurücksetzen
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => users.refetch()}
          disabled={users.isFetching}
        >
          {users.isFetching ? 'Lädt …' : 'Aktualisieren'}
        </button>
      </div>

      {users.isPending && <Spinner label="Benutzer werden geladen …" />}

      {users.isError && (
        <div className="flash flash-error">
          {users.error instanceof ApiError ? users.error.message : 'Liste konnte nicht geladen werden.'}
        </div>
      )}

      {!users.isPending && rows.length === 0 && (
        <p className="muted center pad">Keine Benutzer gefunden.</p>
      )}

      {rows.length > 0 && (
        <div className={`table-wrap ${users.isFetching ? 'is-refreshing' : ''}`}>
          <table className="table">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Welt</th>
                <th>Status</th>
                <th>Rolle</th>
                <th className="right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isSelf={user.id === currentUser.id}
                  worlds={worlds}
                  onMessage={toast.push}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function UserRow({
  user,
  isSelf,
  worlds,
  onMessage,
}: {
  user: User;
  isSelf: boolean;
  worlds: WorldOption[];
  onMessage: (kind: 'success' | 'error' | 'info', message: string) => void;
}) {
  const update = useUpdateUser();
  const remove = useDeleteUser();
  const resetPassword = useResetUserPassword();
  const revokeToken = useRevokeUserToken();
  const [newPassword, setNewPassword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [targetWorld, setTargetWorld] = useState(user.world?.key ?? '');
  const [targetRole, setTargetRole] = useState<UserRole>(user.role);

  const busy =
    update.isPending || remove.isPending || resetPassword.isPending || revokeToken.isPending;

  const fail = (error: Error) => onMessage('error', error.message);

  const changeStatus = (status: UserStatus, done: string) => {
    update.mutate(
      { id: user.id, status },
      { onSuccess: () => onMessage('success', done), onError: fail },
    );
  };

  const applyRole = () => {
    update.mutate(
      { id: user.id, role: targetRole },
      {
        onSuccess: () => {
          onMessage('success', `${user.username} ist jetzt ${ROLE_LABELS[targetRole]}.`);
          setMenuOpen(false);
        },
        onError: (error) => {
          setTargetRole(user.role);
          fail(error);
        },
      },
    );
  };

  const moveWorld = () => {
    const next = targetWorld === '' ? null : targetWorld;
    update.mutate(
      { id: user.id, worldKey: next },
      {
        onSuccess: (data) => {
          onMessage(
            'success',
            data.user.world
              ? `${user.username} gehört jetzt zu "${data.user.world.name}".`
              : `${user.username} ist jetzt weltübergreifend.`,
          );
          setMenuOpen(false);
        },
        onError: (error) => {
          setTargetWorld(user.world?.key ?? '');
          fail(error);
        },
      },
    );
  };

  const revoke = () => {
    if (!window.confirm(`Usertoken von ${user.username} widerrufen? Verbundene Userscripts verlieren sofort den Zugriff.`)) {
      return;
    }
    revokeToken.mutate(
      { id: user.id },
      {
        onSuccess: () => onMessage('success', `Token von ${user.username} wurde widerrufen.`),
        onError: fail,
      },
    );
  };

  const submitPassword = () => {
    resetPassword.mutate(
      { id: user.id, newPassword },
      {
        onSuccess: () => {
          onMessage(
            'success',
            `Passwort von ${user.username} gesetzt. Offene Sitzungen wurden beendet.`,
          );
          setNewPassword('');
          setMenuOpen(false);
        },
        onError: fail,
      },
    );
  };

  const confirmDelete = () => {
    if (!window.confirm(`${user.username} endgültig löschen? Das kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    remove.mutate(
      { id: user.id },
      {
        onSuccess: () => onMessage('success', `${user.username} wurde gelöscht.`),
        onError: fail,
      },
    );
  };

  return (
    <>
      <tr className={user.status === 'pending' ? 'row-pending' : undefined}>
        <td>
          <strong>{user.username}</strong>
          {isSelf && <span className="tag">du</span>}
        </td>
        <td>
          {user.world ? (
            <span className="world-cell">
              <strong>{user.world.name}</strong>
              <code className="world-key">{user.world.key}</code>
            </span>
          ) : (
            <span className="badge badge-global">weltübergreifend</span>
          )}
        </td>
        <td>
          <StatusBadge status={user.status} />
        </td>
        <td>{ROLE_LABELS[user.role]}</td>
        <td className="right">
          <div className="actions">
            {user.status !== 'active' && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => changeStatus('active', `${user.username} wurde freigeschaltet.`)}
              >
                Freischalten
              </button>
            )}

            {user.status !== 'disabled' && !isSelf && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`${user.username} wirklich sperren?`)) {
                    changeStatus('disabled', `${user.username} wurde gesperrt.`);
                  }
                }}
              >
                Sperren
              </button>
            )}

            <button
              type="button"
              className="btn btn-ghost btn-sm"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? 'Weniger ▲' : 'Mehr ▾'}
            </button>
          </div>
        </td>
      </tr>

      {/* Aufklappzeile statt Overlay -- der Scroll-Container der Tabelle
          wuerde ein absolut positioniertes Menue abschneiden. */}
      {menuOpen && (
        <tr className="row-detail">
          <td colSpan={5}>
            <div className="detail-panel">
              <div className="detail-block">
                <span className="detail-title">Konto</span>
                <dl className="detail-facts">
                  <div>
                    <dt>Registriert</dt>
                    <dd>{formatDate(user.createdAt, true)}</dd>
                  </div>
                  <div>
                    <dt>Letzte Anmeldung</dt>
                    <dd>{formatDate(user.lastLoginAt, true)}</dd>
                  </div>
                </dl>
              </div>

              <div className="detail-block">
                <span className="detail-title">Usertoken</span>
                {user.token ? (
                  <>
                    <div className="token-cell">
                      <code>{user.token.prefix}…</code>
                      <small className="hint">
                        zuletzt genutzt: {formatDate(user.token.lastUsedAt, true)}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={busy}
                      onClick={revoke}
                    >
                      Widerrufen
                    </button>
                  </>
                ) : (
                  <span className="muted">kein Token</span>
                )}
              </div>

              <div className="detail-block">
                <span className="detail-title">Rolle</span>
                <div className="detail-row">
                  <select
                    value={targetRole}
                    disabled={busy}
                    onChange={(event) => setTargetRole(event.target.value as UserRole)}
                  >
                    <option value="user">{ROLE_LABELS.user}</option>
                    <option value="tribeLeader">{ROLE_LABELS.tribeLeader}</option>
                    <option value="admin">{ROLE_LABELS.admin}</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || targetRole === user.role}
                    onClick={applyRole}
                  >
                    Übernehmen
                  </button>
                </div>
              </div>

              <div className="detail-block">
                <span className="detail-title">Welt</span>
                <div className="detail-row">
                  <select
                    value={targetWorld}
                    disabled={busy}
                    onChange={(event) => setTargetWorld(event.target.value)}
                  >
                    {user.role === 'admin' && <option value="">Weltübergreifend</option>}
                    {worlds.map((world) => (
                      <option key={world.key} value={world.key}>
                        {world.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || targetWorld === (user.world?.key ?? '')}
                    onClick={moveWorld}
                  >
                    Verschieben
                  </button>
                </div>
                <small className="hint">Beendet die offenen Sitzungen des Kontos.</small>
              </div>

              <div className="detail-block">
                <span className="detail-title">Passwort neu setzen</span>
                <div className="detail-row">
                  <input
                    type="password"
                    placeholder="Neues Passwort (min. 10 Zeichen)"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || newPassword.length === 0}
                    onClick={submitPassword}
                  >
                    Setzen
                  </button>
                </div>
                <small className="hint">Beendet alle offenen Sitzungen dieses Kontos.</small>
              </div>

              {!isSelf && (
                <div className="detail-block">
                  <span className="detail-title">Konto entfernen</span>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={busy}
                    onClick={confirmDelete}
                  >
                    Löschen
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
